import React, { Fragment, useEffect, useState } from "react";
import { Container, Row, Col, Alert, Badge, Button } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import { useAuth } from "../context/AuthContext";
import AxisSpinner from "./AxisSpinner";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import XLSX from "xlsx";
import { ButtonModalOnClickClose } from "./ButtonModalSelection";
import { FaDownload, FaSearch, FaTable } from "react-icons/fa";

export const AuditReportViewer = ({ report, close, actionType = "audit_file" }) => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [excelUrl, setExcelUrl] = useState(null);
  const [tab, setTab] = useState(0);
  const [headings, setHeadings] = useState([]);
  const [csv, setCsv] = useState(null);

  useEffect(() => {
    loadReport();
  }, [report, tab]);

  const download = async () => {
    // Sort reports to ensure consistency
    const sortedReports = [...(report?.reports || [])].sort();
    saveAs(excelUrl, sortedReports[tab]);
  };

  const loadReport = async () => {
    setLoading(true);
    setCsv(null);
    setHeadings([]);
    setExcelUrl(null);
    
    try {
      // Sort reports to ensure consistency
      const sortedReports = [...(report?.reports || [])].sort();
      if (!sortedReports || sortedReports.length === 0 || tab >= sortedReports.length) {
        setLoading(false);
        return;
      }
      
      var params = {
        filename: sortedReports[tab],
        reportDate: report?.month,
      };
      const config = {
        responseType: "arraybuffer",
        params: params,
      };

      let endpoint = `/api/backoffice/reporting/${actionType}`;
      const response = await authAxios().get(
        endpoint,
        config,
      );

      var blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      setExcelUrl(window.URL.createObjectURL(blob));

      const wb = XLSX.read(response.data);
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const csv = XLSX.utils.sheet_to_csv(ws);
      const results = Papa.parse(csv, { header: true });
      const rows = results.data.filter(row => Object.values(row).some(val => val));
      
      if (rows.length > 0) {
        const headings = Object.keys(rows[0] || {}).filter(h => h.trim() !== '');
        setHeadings(headings);
        setCsv(rows);
      } else {
        setHeadings([]);
        setCsv([]);
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error("Error loading report:", err);
    }
  };

  const SpinnerOrData = (loading) => {
    if (loading) return <AxisSpinner loading={loading} />;
    
    if (!csv || csv.length === 0) {
      return (
        <Alert variant="info">
          No data available for this report
        </Alert>
      );
    }

    return (
      <div
        className="table-responsive"
        style={{
          maxHeight: window.screen.height * 0.6 + "px",
          overflowY: "auto",
          overflowX: "auto",
          borderRadius: "4px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.1)"
        }}
      >
        <Table striped bordered hover responsive>
          <thead className="bg-light">
            <tr>
              {headings?.map((heading, index) => (
                <th key={index} style={{position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1}}>
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {csv?.map((row, index) => (
              <tr key={index}>
                {headings?.map((heading, headingIndex) => (
                  <td key={`${index}-${headingIndex}`}>{row[heading]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <Fragment>
      {/* Header with report information */}
      <Container fluid className="mb-3">
        <Row>
          <Col>
            <h5 className="mb-3">
              <FaTable className="me-2" />
              {actionType === "audit_file" ? "Audit Report" : 
               actionType === "missed_availability_file" ? "Missed Availability Report" : 
               "Availability Bookings Report"}
              {report?.month && (
                <Badge bg="secondary" className="ms-2">
                  {new Date(report.month).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                </Badge>
              )}
            </h5>
          </Col>
          <Col xs="auto">
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={download} 
              disabled={loading || !excelUrl}
              className="me-2"
            >
              <FaDownload className="me-1" /> Download Excel
            </Button>
          </Col>
        </Row>
      </Container>

      {/* Report tabs */}
      <Tabs
        id="reporting-tabs"
        className={"mb-3 axis-table-tabs nav-fill"}
        activeKey={tab}
        onSelect={(k) => setTab(k)}
        variant="pills"
      >
        {report?.reports ? [...report.reports].sort().map((reportName, index) => (
          <Tab eventKey={index} title={reportName} key={index} />
        )) : null}
      </Tabs>

      {/* Report data */}
      <Container fluid className="mb-4">{SpinnerOrData(loading)}</Container>
      
      {/* Report stats */}
      {!loading && csv && csv.length > 0 && (
        <Container fluid className="mb-3">
          <small className="text-muted">
            Showing {csv.length} records in {headings.length} columns
          </small>
        </Container>
      )}

      {/* Action buttons */}
      <ButtonModalOnClickClose
        onClickFunc={download}
        loading={loading}
        close={close}
        funcButtonName={"Download"}
      />
    </Fragment>
  );
};
